import SvgIcon from '@/pages/home/components/SvgIcon';
import React, { useCallback, useMemo } from 'react';

import '@/pages/opinion/assets/scss/pages/performance/components/cardContext.scss';
import { keyMetricsType } from '../../types/performance';
import BubbleCommon, {
  accountPostsTips,
  consoleUserScoreTips,
  engagementTips,
  googleText,
  mentionsTips,
  nonNegativeTips,
  newPcUserScoreTips,
  TipsMapType,
  commonSentimentTips,
  commonBrandHealthTips,
  commonBrandHealthGradeTips,
} from '../../components/BubbleCommon';
import { gameInfoType } from '../../types/common';
import { getShowCount, isOneDay } from '../../utils';
import moment from 'moment';
import { useIntl, getLocale } from 'umi';

import googlePlay from '@/pages/opinion/assets/svg/channel/google_play.svg';
import steam from '@/pages/opinion/assets/svg/channel/steam.svg';
import metacritic from '@/pages/opinion/assets/svg/channel/metacritic.svg';
import CardSlot from '../../components/CardSlot';
import RingRatioBubble from '../../components/Snippets/RingRatioBubble';
import {
  brandHealthFormat,
  reviewsFormat,
  sentimentFormat,
} from '@op/utils/value';
import BrandHealthCharts from '@/pages/opinion/components/Dashboard/BrandHealthCharts';
import { calculateBrandHealthRate } from '@op/utils/text';

type NameChooseType = {
  tipsMap?: TipsMapType;
  icon?: string;
  title: string;
  className?: string;
  placement?: string;
};

export default function MetricsCard(props: {
  data: keyMetricsType;
  gameInfo: gameInfoType;
  filter: any;
  time_period: any;
  isLast: boolean;
  titleMap: {
    [key: string]: NameChooseType;
  };
}) {
  const { data, gameInfo, filter, time_period, titleMap, isLast } = props;

  const { formatMessage } = useIntl();

  const nameChooseMap: {
    [key: string]: NameChooseType;
  } = useMemo(() => {
    if (titleMap) return titleMap;

    return {
      brand_health: {
        title: formatMessage({ id: 'Brand Health' }),
      },
      // brand_health: {
      //   tipsMap: commonBrandHealthTips,
      //   title: formatMessage({ id: 'Brand Health' }),
      // },
      mentions: {
        tipsMap: mentionsTips,
        title: formatMessage({ id: 'Mentions' }),
      },
      account_posts: {
        tipsMap: accountPostsTips,
        title: formatMessage({ id: 'Account Posts' }),
      },
      engagement: {
        tipsMap: engagementTips,
        title: formatMessage({ id: 'Engagement' }),
      },
      avg_sentiment: {
        tipsMap: commonSentimentTips,
        isIconTips: true,
        title: formatMessage({ id: 'Sentiment' }),
        className: 'avg_sentiment',
      },
      non_negative_ratio: {
        tipsMap: nonNegativeTips,
        title: formatMessage({ id: 'Non Negative%' }),
      },
      mobile: {
        tipsMap: googleText,
        icon: googlePlay,
        title: formatMessage({ id: 'Lifetime User Score' }),
        placement: 'top-end',
      },
      pc: {
        tipsMap: newPcUserScoreTips,
        icon: steam,
        title: formatMessage({ id: 'Selected Positive%' }),
        placement: 'top-end',
      },
      console: {
        tipsMap: consoleUserScoreTips,
        icon: metacritic,
        title: formatMessage({ id: 'User Score' }),
        placement: 'top-end',
      },
    };
  }, [formatMessage, titleMap]);

  const bubbleChoose = useCallback(
    (
      name: string,
      getElement?: (value: NameChooseType) => JSX.Element,
      showIcon = false,
    ) => {
      const key: string = !['steam_selected_score', 'user_score'].includes(name)
        ? name
        : gameInfo.entity_type;
      const item = nameChooseMap[key || 'mobile'] || {};
      const { tipsMap = null, placement = 'top-start' } = item;
      return (
        <BubbleCommon
          dark
          placement={isLast ? 'top-center' : placement}
          tipsMap={tipsMap}
          showIcon={showIcon}
        >
          {getElement?.(item)}
        </BubbleCommon>
      );
    },
    [nameChooseMap, gameInfo],
  );

  const getTitleElement = (
    item: NameChooseType,
    noIcon: boolean = false,
    element: JSX.Element | null = null,
  ) => {
    const { icon, className, title } = item || {};
    return (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {!noIcon && icon && (
          <img src={icon} className="dbo-cardcontext-title-icon" />
        )}
        <span
          className={`dbo-cardcontext-title-main ${
            className ? `dbo-cardcontext-title-main ${className}` : ''
          }`}
        >
          {title}
          {element}
        </span>
      </div>
    );
  };

  const nameChoose = (name: string, noIcon: boolean = false) => {
    return bubbleChoose(name, (item) => {
      return getTitleElement(item, noIcon);
    });
  };

  const hotBoxElementRenderMap: any = {
    net_sentiment: (name: string) => {
      const item = nameChooseMap.net_sentiment;
      return (
        <div className="dbo-hot-box">
          <div className="dbo-cardcontext-title">
            {getTitleElement(item, false, bubbleChoose(name, undefined, true))}
          </div>
          <div className="dbo-cardcontext-num">
            {numberType(name, data.value)}
          </div>
        </div>
      );
    },
    brand_health: (name: string) => {
      const value = numberType(name, data.value);
      const isNoData = value === '-';
      return bubbleChoose(name, (item) => {
        const { text, className } = isNoData
          ? { text: '', className: '' }
          : calculateBrandHealthRate(Number(value));
        return (
          <div className="dbo-hot-box">
            <div className="dbo-hot-box-info">
              <BubbleCommon
                dark
                placement="top"
                tipsMap={commonBrandHealthTips}
                showIcon={false}
              >
                <div className="dbo-cardcontext-title">
                  {getTitleElement(item)}
                </div>
              </BubbleCommon>

              <div className="dbo-cardcontext-num">
                {!isNoData ? (
                  <BubbleCommon
                    dark
                    placement="top"
                    tipsMap={commonBrandHealthGradeTips(text)}
                    showIcon={false}
                  >
                    <span
                      className={`dbo-cardcontext-num-healthy ${className}`}
                    >
                      {formatMessage({ id: text })}
                    </span>
                  </BubbleCommon>
                ) : (
                  '-'
                )}
              </div>
            </div>
            {!isNoData && (
              <div className="dbo-hot-box-chart">
                <BrandHealthCharts brandHealth={Number(value)} />
                <div className="dbo-hot-box-chart-num">-100~100</div>
              </div>
            )}
          </div>
        );
      });
    },
    common: (name: string) => {
      return bubbleChoose(name, (item) => {
        const value = numberType(name, data.value);
        return (
          <div className="dbo-hot-box">
            <div className="dbo-hot-box-info">
              <div className="dbo-cardcontext-title">
                {getTitleElement(item)}
              </div>
              <div className="dbo-cardcontext-num">
                {value}
                <span
                  style={{
                    marginLeft: 8,
                    color: '#888888',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {name === 'avg_sentiment' && formatMessage({ id: 'Up to 5' })}
                </span>
              </div>
            </div>
          </div>
        );
      });
    },
  };

  const getHotBoxElement = (name: string) => {
    const func = hotBoxElementRenderMap[name] || hotBoxElementRenderMap.common;

    return func(name);
  };

  const numberType = (name: string, value: number) => {
    if (value === 0)
      return ['mentions', 'engagement', 'brand_health'].includes(name)
        ? 0
        : '-';

    if (name === 'non_negative_ratio') {
      return Math.round(value * 100) + '%';
    }
    if (['steam_selected_score', 'user_score'].includes(name)) {
      if (gameInfo.entity_type === 'pc') {
        return Math.round(value * 100) + '%';
      }
      return value?.toFixed(1);
    }
    if (name === 'avg_sentiment') {
      return sentimentFormat(value);
    }

    if (name === 'brand_health') {
      return brandHealthFormat(value);
    }

    return reviewsFormat(value);
  };

  const timeShow = useCallback(
    (startKey: string, endKey: string) => {
      const start_time = time_period[startKey],
        endTime = time_period[endKey];
      if (filter.date_type == 'hourly') {
        return (
          moment(start_time).format('YYYY-MM-DD HH:mm') +
          '~' +
          moment(endTime).format('YYYY-MM-DD HH:mm')
        );
      } else {
        if (isOneDay(filter)) {
          return moment(start_time).format('YYYY-MM-DD');
        } else {
          return (
            moment(start_time).format('YYYY-MM-DD') +
            '~' +
            moment(endTime).format('YYYY-MM-DD')
          );
        }
      }
    },
    [time_period, filter],
  );

  return (
    <CardSlot className="dbo-cardcontexts">
      {getHotBoxElement(data.metric_name)}

      <RingRatioBubble
        otherInfo={
          <div className="item-title">
            {nameChoose(data.metric_name, true)}
            {getLocale() === 'en-US' ? <>&nbsp;</> : ''}
            {formatMessage({
              id: isOneDay(filter) ? 'DoD' : 'Change Rate',
            })}
          </div>
        }
        newTime={[timeShow('start_date', 'end_date')]}
        oldTime={[timeShow('pre_start_date', 'pre_end_date')]}
        oldValue={numberType(data.metric_name, data.pre_value)}
        newValue={numberType(data.metric_name, data.value)}
        placement="top-start"
      >
        <div className="dbo-cardcontext-flex">
          <span className="context-text">
            {formatMessage({
              id: isOneDay({
                ...filter,
              })
                ? 'DoD'
                : 'Change',
            })}
          </span>
          <span
            className={
              data.value > data.pre_value
                ? 'context-success-text'
                : data.value === data.pre_value
                ? ''
                : 'context-red-text'
            }
          >
            {data.dod == 0
              ? '-'
              : data.dod < 0
              ? data.dod.toFixed(2).toString()?.substring(1) + '%'
              : data.dod.toFixed(2) + '%'}
          </span>
          {data.value !== data.pre_value && (
            <SvgIcon
              type={
                data.value > data.pre_value ? 'green-up-new' : 'red-down-new'
              }
              className="dbo-cardcontext-icon"
            />
          )}
        </div>
      </RingRatioBubble>
    </CardSlot>
  );
}
